/**
 * Shared analytics attribute + event vocabulary for Dash0.
 *
 * Pure constants and types only — no `@dash0/sdk-web` or OpenTelemetry imports —
 * so the *same* names describe browser web events (`sendEvent`) and server span
 * events/attributes. Centralising them lets a web event, its backend span, and
 * the request logs be cross-referenced (join on `user.id` / `app.plan`) and gives
 * Dash0's AI chat a stable, predictable vocabulary to query over.
 *
 * Attribute keys follow OpenTelemetry semantic conventions where one exists
 * (`user.id`, `user.roles`); app-specific dimensions are namespaced to avoid
 * collisions with platform attributes.
 */

// Identity + core dimensions.
export const ATTR_USER_ID = 'user.id';
export const ATTR_USER_ROLES = 'user.roles';
export const ATTR_APP_PLAN = 'app.plan';
export const ATTR_APP_ORG = 'app.org';
export const ATTR_MCP_TOOL = 'mcp.tool';
export const ATTR_OUTCOME = 'app.outcome';

// Event-specific dimensions.
export const ATTR_RESULT_KIND = 'app.result_kind';
export const ATTR_INTEGRATION_PROVIDER = 'integration.provider';
export const ATTR_PLAN_SCOPE = 'plan.scope';
export const ATTR_PLAN_VALUE = 'plan.value';
export const ATTR_PARTIAL = 'app.partial';
// Metering (issue #1941). `meter.*` is app-specific; the token/model dimensions
// use OpenTelemetry's GenAI semantic convention, per this file's policy of
// preferring a real convention where one exists — which also means Dash0's
// GenAI dashboards understand them without configuration.
export const ATTR_METER = 'meter.name';
export const ATTR_METER_QUANTITY = 'meter.quantity';
export const ATTR_METER_COST_NANO_USD = 'meter.cost_nano_usd';
export const ATTR_METER_COST_STATUS = 'meter.cost_status';
/** False when a call could not be attributed to a tenant — see `usage.recorded`. */
export const ATTR_METER_ATTRIBUTED = 'meter.attributed';
/**
 * Which product feature drove the call (`FEATURES` in `@libs/usage-metering`).
 * Absent when the call site named none — which is itself the signal that a code
 * path is metered but unlabelled. Without this, Dash0 can say spend rose but not
 * which feature raised it, and "which feature" is the actionable half.
 */
export const ATTR_METER_FEATURE = 'meter.feature';
// `gen_ai.provider.name`, not the deprecated `gen_ai.system`.
export const ATTR_GEN_AI_PROVIDER = 'gen_ai.provider.name';
/**
 * The attribute GenAI tooling actually keys on. Without it a span carrying the
 * other `gen_ai.*` keys is invisible to those views — which is exactly what
 * happened when this shipped as span events alone: Dash0 found nothing.
 */
export const ATTR_GEN_AI_OPERATION = 'gen_ai.operation.name';
export const ATTR_GEN_AI_RESPONSE_MODEL = 'gen_ai.response.model';
export const ATTR_GEN_AI_MODEL = 'gen_ai.request.model';
export const ATTR_GEN_AI_INPUT_TOKENS = 'gen_ai.usage.input_tokens';
export const ATTR_GEN_AI_OUTPUT_TOKENS = 'gen_ai.usage.output_tokens';

export const ATTR_COMPETENCE_OWNER = 'competence.owner';
export const ATTR_COMPETENCE_REPO = 'competence.repo';
export const ATTR_COMPETENCE_WINDOW = 'competence.window';

/** `app.outcome` values. */
export const OUTCOME_OK = 'ok';
export const OUTCOME_ERROR = 'error';

export interface Identity {
  readonly userId: string;
  /** `'free' | 'paid'` — omit when the caller has not resolved paid access. */
  readonly plan?: string;
  /** GitHub org login, when the action is org-scoped. */
  readonly org?: string;
}

/**
 * Build the identity attribute bag stamped onto signals. Only defined values are
 * included, so callers may omit `plan`/`org` rather than send blank strings. The
 * plain `Record<string, string>` is assignable to both OpenTelemetry `Attributes`
 * and the Dash0 web-event attribute type.
 */
export function buildIdentityAttributes(
  identity: Identity,
): Record<string, string> {
  return {
    [ATTR_USER_ID]: identity.userId,
    ...(identity.plan === undefined ? {} : { [ATTR_APP_PLAN]: identity.plan }),
    ...(identity.org === undefined ? {} : { [ATTR_APP_ORG]: identity.org }),
  };
}

/** Client web events, emitted from the browser via `@dash0/sdk-web` `sendEvent`. */
export const WEB_EVENTS = {
  githubSignInClicked: 'github_sign_in_clicked',
  integrationConnected: 'integration_connected',
  planChanged: 'plan_changed',
  workItemsViewed: 'work_items_viewed',
  reviewsViewed: 'reviews_viewed',
  competenceViewed: 'competence_viewed',
  competenceCompleted: 'competence_completed',
} as const;

export type WebEvent = (typeof WEB_EVENTS)[keyof typeof WEB_EVENTS];

/**
 * Human-readable titles sent with each web event. Dash0 surfaces these via the
 * `dash0.web.event.title` attribute, which keeps the events legible in the UI and
 * the AI chat. A `Map` (not a record) so lookups don't trip the object-injection rule.
 */
export const WEB_EVENT_TITLES: ReadonlyMap<WebEvent, string> = new Map<
  WebEvent,
  string
>([
  [WEB_EVENTS.githubSignInClicked, 'GitHub sign-in clicked'],
  [WEB_EVENTS.integrationConnected, 'Integration connected'],
  [WEB_EVENTS.planChanged, 'Plan changed'],
  [WEB_EVENTS.workItemsViewed, 'Work items viewed'],
  [WEB_EVENTS.reviewsViewed, 'Reviews viewed'],
  [WEB_EVENTS.competenceViewed, 'Competence profile viewed'],
  [WEB_EVENTS.competenceCompleted, 'Competence profile completed'],
]);

/** Server span-event names, recorded on the active OpenTelemetry span. */
export const SERVER_EVENTS = {
  mcpToolInvoked: 'mcp.tool.invoked',
  workItemsViewed: 'account.work_items.viewed',
  nextWorkViewed: 'account.next_work.viewed',
  reviewsViewed: 'account.reviews.viewed',
  planChanged: 'account.plan.changed',
  integrationConnected: 'account.integration.connected',
  competencePrepared: 'competence.prepared',
  competenceAnalyzed: 'competence.analyzed',
  /**
   * One metered provider call. Emitted for EVERY call, including ones that
   * cannot be attributed to a tenant or priced — those carry
   * `meter.attributed = false` / `meter.cost_status = 'unpriced'` so a coverage
   * gap is a queryable number rather than silence.
   */
  usageRecorded: 'meter.usage.recorded',
} as const;

export type ServerEvent = (typeof SERVER_EVENTS)[keyof typeof SERVER_EVENTS];
